import { useState } from "react";
import { Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useSubscription,
  useCreateCheckoutSession,
  useCreatePortalSession,
} from "@/hooks/useBilling";
import { cn } from "@/lib/utils";

type Interval = "monthly" | "yearly";

interface PlanDef {
  code: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyPriceId: string;
  yearlyPriceId: string;
  features: string[];
  recommended?: boolean;
}

const PLANS: PlanDef[] = [
  {
    code: "STARTER",
    name: "Starter",
    monthlyPrice: 49,
    yearlyPrice: 490,
    monthlyPriceId: "price_1TKMuNCaTtyRbHqF6zF4veB2",
    yearlyPriceId: "price_1TKMvuCaTtyRbHqFaKljFSW0",
    features: ["100 Produkte", "1 Quiz", "E-Mail-Support"],
  },
  {
    code: "PROFESSIONAL",
    name: "Professional",
    monthlyPrice: 99,
    yearlyPrice: 990,
    monthlyPriceId: "price_1TKMxiCaTtyRbHqFhAi1TWVO",
    yearlyPriceId: "price_1TKMyDCaTtyRbHqF656p3GCQ",
    features: ["1.000 Produkte", "5 Quizze", "Priority Support"],
    recommended: true,
  },
  {
    code: "ENTERPRISE",
    name: "Enterprise",
    monthlyPrice: 299,
    yearlyPrice: 2990,
    monthlyPriceId: "price_1TKMyvCaTtyRbHqFp8rnAwK1",
    yearlyPriceId: "price_1TKMzgCaTtyRbHqFjMcW6Qtd",
    features: [
      "Unbegrenzte Produkte",
      "Unbegrenzte Quizze",
      "Priority Support",
    ],
  },
];

const formatEur = (cents: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(cents);

export function PricingPage() {
  const [interval, setInterval] = useState<Interval>("monthly");
  const { data: subscription } = useSubscription();
  const checkout = useCreateCheckoutSession();
  const portal = useCreatePortalSession();

  const handleSelectPlan = (plan: PlanDef) => {
    const priceId =
      interval === "monthly" ? plan.monthlyPriceId : plan.yearlyPriceId;
    checkout.mutate(priceId, {
      onSuccess: (data) => {
        window.location.href = data.url;
      },
    });
  };

  const currentPlan = subscription?.planCode ?? "FREE";
  const hasStripeCustomer = subscription?.hasStripeCustomer ?? false;

  const handleManageSubscription = () => {
    portal.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.url;
      },
    });
  };

  return (
    <div className="space-y-8 p-8">
      {subscription?.billingStatus === "PAST_DUE" && (
        <div className="bg-destructive/10 border-destructive text-destructive rounded-md border px-4 py-3 text-sm">
          <strong>Zahlungsproblem:</strong> Deine letzte Zahlung ist
          fehlgeschlagen. Bitte aktualisiere deine Zahlungsmethode, um eine
          Unterbrechung des Dienstes zu vermeiden.
          <Button
            variant="link"
            className="ml-2 h-auto p-0"
            onClick={handleManageSubscription}
          >
            Zahlungsmethode aktualisieren
          </Button>
        </div>
      )}

      {(subscription?.billingStatus === "CANCELLED" ||
        subscription?.billingStatus === "SUSPENDED") && (
        <div className="bg-muted rounded-md border px-4 py-3 text-sm">
          <strong>Abonnement inaktiv:</strong> Ihr Zugang ist auf den Lesemodus
          beschränkt. Wählen Sie einen Plan, um wieder Änderungen vornehmen zu
          können.
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Abonnement</h1>
          <p className="text-muted-foreground mt-1">
            Wählen Sie den passenden Plan für Ihr Unternehmen.
          </p>
        </div>
        {hasStripeCustomer && (
          <Button
            variant="outline"
            onClick={handleManageSubscription}
            disabled={portal.isPending}
          >
            {portal.isPending ? "Wird geladen..." : "Abo verwalten"}
            {!portal.isPending && <ExternalLink className="ml-2 h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Interval Toggle */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setInterval("monthly")}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
            interval === "monthly"
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-accent",
          )}
        >
          Monatlich
        </button>
        <button
          onClick={() => setInterval("yearly")}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
            interval === "yearly"
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-accent",
          )}
        >
          Jährlich
          <span className="ml-1 text-xs opacity-75">(2 Monate gratis)</span>
        </button>
      </div>

      {/* Plan Cards */}
      <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
        {PLANS.map((plan) => {
          const isCurrentPlan = currentPlan === plan.code;
          const price =
            interval === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
          const suffix = interval === "monthly" ? "/Monat" : "/Jahr";

          return (
            <Card
              key={plan.code}
              className={cn(
                "relative flex flex-col",
                plan.recommended && "border-primary shadow-lg",
              )}
            >
              {plan.recommended && (
                <div className="bg-primary text-primary-foreground absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-medium">
                  Empfohlen
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>
                  <span className="text-foreground text-3xl font-bold">
                    {formatEur(price)}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {suffix}
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-2">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Check className="text-primary h-4 w-4" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {isCurrentPlan ? (
                  <Button variant="secondary" className="w-full" disabled>
                    Aktueller Plan
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant={plan.recommended ? "default" : "outline"}
                    onClick={() => handleSelectPlan(plan)}
                    disabled={checkout.isPending}
                  >
                    {checkout.isPending ? "Wird geladen..." : "Plan wählen"}
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {checkout.isError && (
        <p className="text-destructive text-center text-sm">
          Fehler beim Erstellen der Checkout-Session. Bitte versuchen Sie es
          erneut.
        </p>
      )}

      {portal.isError && (
        <p className="text-destructive text-center text-sm">
          Fehler beim Öffnen der Abo-Verwaltung. Bitte versuchen Sie es erneut.
        </p>
      )}
    </div>
  );
}
